<template>
  <div
    class="gauge-container"
    :class="{ inline: inline }">
    <canvas ref="canvas" />
    <div
      v-if="isValueSlotSupplied"
      class="value">
      <slot name="value" />
    </div>
    <span
      v-else
      class="value"
      >{{ formatter ? formatter(value) : formatNumber(value, '0%') }}</span
    >
    <div
      v-if="isLabelSlotSupplied"
      class="label">
      <slot name="label" />
    </div>
  </div>
</template>

<script setup lang="ts">
  //#region Imports
  import { computed, onMounted, Ref, ref, useSlots, watch } from 'vue';

  import formatNumber from '@/utils/NumberFormatter';

  //#endregion

  //#region Props

  interface IColorStop {
    percentage: number;
    color: string;
  }

  interface IGaugeProps {
    label?: string;
    value: number;
    min: number;
    max: number;
    inline?: boolean;
    colorStops: IColorStop[];
    formatter?: (value: number) => string;
  }

  const props = withDefaults(defineProps<IGaugeProps>(), {
    value: 0.7,
    min: 0,
    max: 1,
    inline: false,
    colorStops: () => [
      { percentage: 0, color: '#ff0083' },
      { percentage: 0.5, color: '#FFDF02' },
      { percentage: 1, color: '#00e074' },
    ],
  });

  const slots = useSlots();

  //#endregion

  //#region Data

  const canvas: Ref<HTMLCanvasElement> = ref(null);
  let interval = null;

  //#endregion

  //#region Mapped actions, state, getters

  // const {} = mapGetters();
  // const {} = mapOrgAppGetters();
  // const {} = mapActions();
  // const {} = mapOrgAppActions();

  //#endregion

  //#region Computed

  const isLabelSlotSupplied = computed(() => !!slots.label);
  const isValueSlotSupplied = computed(() => !!slots.value);

  //#endregion

  //#region Functions

  function draw() {
    const ctx = canvas.value.getContext('2d');
    canvas.value.width = 500;
    canvas.value.height = 500;
    const dpr = window.devicePixelRatio || 1;
    const bsr = 1;

    const pixelRatio = dpr / bsr;
    ctx.lineWidth = 1 * pixelRatio;

    const canvasWidth = canvas.value.width;
    const canvasHeight = canvas.value.height;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const arcRadius = canvasWidth / 2.5;
    const circleRadius = canvasWidth / 2;
    const innerCircleRadius = props.inline ? canvasWidth / 4 : canvasWidth / 3.4;
    const startAngle = Math.PI * 1.5;

    const gradient = ctx.createConicGradient(startAngle, centerX, centerX);
    props.colorStops.forEach((cs) => gradient.addColorStop(cs.percentage, cs.color));

    // ctx.fillStyle = '#00000007';
    // ctx.beginPath();
    // ctx.ellipse(centerX, centerY + 2, shadowRadius, shadowRadius, 0, 0, 2 * Math.PI);
    // ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, circleRadius, circleRadius, 0, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = '#0000000a';
    ctx.strokeStyle = '#00000040';
    ctx.beginPath();
    ctx.ellipse(centerX, centerY + 2, innerCircleRadius, innerCircleRadius, 0, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    ctx.lineWidth = props.inline ? canvasWidth / 4 : canvasWidth / 8;
    ctx.strokeStyle = gradient;

    const percentage = (props.value - props.min) / (props.max - props.min);
    ctx.beginPath();
    ctx.arc(centerX, centerY, arcRadius, startAngle, startAngle + Math.PI * 2 * percentage);
    ctx.stroke();
  }

  watch(
    () => props.value,
    (newValue, oldValue) => {
      draw();
    },
  );

  //#endregion

  //#region Lifecycle hooks

  onMounted(() => {
    interval = setInterval(() => {
      if (canvas.value && canvas.value.getContext('2d')) {
        clearInterval(interval);
        draw();
      }
    }, 100);
  });

  //#endregion

  //#region Initialization

  //#endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;

  .gauge-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .label {
      position: absolute;
      bottom: -1.6rem;
      background-color: #555;
      color: #fff;
      border-radius: 0.6rem;
      padding: 0.2rem 0.6rem;
      font-weight: 900;
      box-shadow: 0 0.2rem 0.6rem #0003;
      border: solid 0.2rem #fff;
      font-size: 1rem;
    }

    .value {
      position: absolute;
    }

    canvas {
      //width: 100%;
      height: 100%;
      border-radius: 100%;
      box-shadow: 0 0.3rem 1rem #00000010;
      border: solid 0.1rem #ddd;
      color: var(--success);
    }

    &.inline {
      position: initial;
      .value {
        position: initial;
      }

      canvas {
        border: none;
        box-shadow: none;
        margin-right: 0.6rem;
      }
    }
  }
</style>
